import { HttpError, postJson } from '../utils/http'
import { create } from 'zustand'
import { ChildBirthday } from '../components/landing-page/availability-tab/type'
import { buildLastMinuteInquiryUrl } from '../components/landing-page/availability-tab/utils'

type LastMinuteInquiryContext = {
  planId: number
  startDate: string
  endDate: string
}

export type StudyAbroadChildInfo = {
  allergyAvailability: string
  allergyDetail: string
  schoolExperience: string
  supportNeeds: string
  supportNeedsDetail: string
}

export type LastMinuteInquiryFormData = {
  agreement: boolean
  representativeName: string
  email: string
  numberOfAdults: number
  numberOfStudyAbroad: number
  numberOfPreschoolers1: number
  numberOfStudents1: number
  birthdays: ChildBirthday[]
  studyAbroadChildren: StudyAbroadChildInfo[]
}

export type LastMinuteInquiryFormErrors = {
  representativeName?: string
  email?: string
  birthdays?: string[]
  studyAbroadChildren?: Array<{
    allergyAvailability?: string
    allergyDetail?: string
    schoolExperience?: string
    supportNeeds?: string
    supportNeedsDetail?: string
  }>
}

type LastMinuteInquiryStore = {
  formData: LastMinuteInquiryFormData
  errors: LastMinuteInquiryFormErrors
  isSubmitting: boolean
  isSubmitted: boolean
  // [#2925] 送信完了画面に出す LINE 友だち追加 URL（問合せ者用の流入経路）。送信成功時のレスポンスで受け取る
  lineFriendUrl: string | null
  context: LastMinuteInquiryContext | null
  initializeLastMinuteInquiry: (context: LastMinuteInquiryContext) => void
  updateFormData: (updates: Partial<LastMinuteInquiryFormData>) => void
  setStudyAbroadChildAt: (index: number, updates: Partial<StudyAbroadChildInfo>) => void
  submitLastMinuteInquiry: () => Promise<boolean>
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const emptyChildInfo = (): StudyAbroadChildInfo => ({
  allergyAvailability: '',
  allergyDetail: '',
  schoolExperience: '',
  supportNeeds: '',
  supportNeedsDetail: '',
})

// [#2925] 人数と生年月日は日程一覧の人数入力（URL クエリ）から引き継ぎ、問合せフォームでは変更させない。
// 「問合せ」は入力した人数・生年月日で園の受入枠に空きがある日だけ出るため（サーバーが送信時にも再評価する）
const buildInitialFormData = (): LastMinuteInquiryFormData => {
  const query = new URLSearchParams(window.location.search)
  const numberOfAdults = Math.max(1, Number(query.get('number_of_adults') || 2))
  const numberOfStudyAbroad = Math.max(1, Number(query.get('number_of_study_abroad') || 1))
  const numberOfPreschoolers1 = Math.max(0, Number(query.get('number_of_preschoolers_1') || 0))
  const numberOfStudents1 = Math.max(0, Number(query.get('number_of_students_1') || 0))
  const parsedBirthdays = (query.get('birthdays') || '')
    .split(',')
    .filter(Boolean)
    .map((birthday) => {
      const [year = '', month = '', day = ''] = birthday.split('-')
      return { year, month, day }
    })

  return {
    agreement: false,
    representativeName: '',
    email: '',
    numberOfAdults,
    numberOfStudyAbroad,
    numberOfPreschoolers1,
    numberOfStudents1,
    birthdays: Array.from(
      { length: numberOfStudyAbroad },
      (_, index) => parsedBirthdays[index] || { year: '', month: '', day: '' },
    ),
    studyAbroadChildren: Array.from({ length: numberOfStudyAbroad }, () => emptyChildInfo()),
  }
}

const validateForm = (formData: LastMinuteInquiryFormData): LastMinuteInquiryFormErrors => {
  const errors: LastMinuteInquiryFormErrors = {}
  if (!formData.representativeName.trim()) errors.representativeName = '必須項目です'
  if (!formData.email.trim()) {
    errors.email = '必須項目です'
  } else if (!EMAIL_REGEX.test(formData.email.trim())) {
    errors.email = 'メールアドレスの形式が正しくありません'
  }

  const birthdayErrors = Array.from({ length: formData.numberOfStudyAbroad }).map((_, idx) => {
    const birthday = formData.birthdays[idx]
    return birthday?.year && birthday?.month && birthday?.day
      ? ''
      : `${idx + 1}人目の生年月日がありません。日程一覧の人数入力で生年月日を入力してください。`
  })
  if (birthdayErrors.some((message) => Boolean(message))) errors.birthdays = birthdayErrors

  const childErrors = Array.from({ length: formData.numberOfStudyAbroad }).map((_, idx) => {
    const child = formData.studyAbroadChildren[idx] || emptyChildInfo()
    const childError: NonNullable<LastMinuteInquiryFormErrors['studyAbroadChildren']>[number] = {}
    if (!child.allergyAvailability) childError.allergyAvailability = '必須項目です'
    if (child.allergyAvailability === '有' && !child.allergyDetail.trim()) childError.allergyDetail = '必須項目です'
    if (!child.schoolExperience) childError.schoolExperience = '必須項目です'
    if (!child.supportNeeds) childError.supportNeeds = '必須項目です'
    if (child.supportNeeds === '有' && !child.supportNeedsDetail.trim()) childError.supportNeedsDetail = '必須項目です'
    return childError
  })
  if (childErrors.some((childError) => Object.keys(childError).length > 0)) {
    errors.studyAbroadChildren = childErrors
  }

  return errors
}

export const useLastMinuteInquiryStore = create<LastMinuteInquiryStore>()((set, get) => ({
  formData: buildInitialFormData(),
  errors: {},
  isSubmitting: false,
  isSubmitted: false,
  lineFriendUrl: null,
  context: null,

  initializeLastMinuteInquiry: (context) =>
    set({
      context,
      formData: buildInitialFormData(),
      errors: {},
      isSubmitting: false,
      isSubmitted: false,
      lineFriendUrl: null,
    }),

  updateFormData: (updates) =>
    set((state) => {
      const nextErrors = { ...state.errors }
      if (Object.prototype.hasOwnProperty.call(updates, 'representativeName')) delete nextErrors.representativeName
      if (Object.prototype.hasOwnProperty.call(updates, 'email')) delete nextErrors.email
      if (Object.prototype.hasOwnProperty.call(updates, 'birthdays')) delete nextErrors.birthdays
      if (Object.prototype.hasOwnProperty.call(updates, 'studyAbroadChildren')) delete nextErrors.studyAbroadChildren
      return {
        errors: nextErrors,
        formData: { ...state.formData, ...updates },
      }
    }),

  setStudyAbroadChildAt: (index, updates) =>
    set((state) => {
      const studyAbroadChildren = [...state.formData.studyAbroadChildren]
      studyAbroadChildren[index] = {
        ...(studyAbroadChildren[index] || emptyChildInfo()),
        ...updates,
      }
      const nextErrors = { ...state.errors }
      if (nextErrors.studyAbroadChildren) {
        const nextChildErrors = [...nextErrors.studyAbroadChildren]
        const current = { ...(nextChildErrors[index] || {}) }
        if (Object.prototype.hasOwnProperty.call(updates, 'allergyAvailability')) {
          delete current.allergyAvailability
          delete current.allergyDetail
        }
        if (Object.prototype.hasOwnProperty.call(updates, 'allergyDetail')) delete current.allergyDetail
        if (Object.prototype.hasOwnProperty.call(updates, 'schoolExperience')) delete current.schoolExperience
        if (Object.prototype.hasOwnProperty.call(updates, 'supportNeeds')) {
          delete current.supportNeeds
          delete current.supportNeedsDetail
        }
        if (Object.prototype.hasOwnProperty.call(updates, 'supportNeedsDetail')) delete current.supportNeedsDetail
        nextChildErrors[index] = current
        if (nextChildErrors.every((childError) => Object.keys(childError || {}).length === 0)) {
          delete nextErrors.studyAbroadChildren
        } else {
          nextErrors.studyAbroadChildren = nextChildErrors
        }
      }
      return {
        errors: nextErrors,
        formData: { ...state.formData, studyAbroadChildren },
      }
    }),

  submitLastMinuteInquiry: async () => {
    const { formData, context } = get()
    if (!formData.agreement) return false

    const validationErrors = validateForm(formData)
    if (Object.keys(validationErrors).length > 0) {
      set({ errors: validationErrors })
      return false
    }
    if (!context) return false

    const birthdays = formData.birthdays
      .filter((birthday) => birthday.year && birthday.month && birthday.day)
      .map((birthday) => `${birthday.year}-${birthday.month}-${birthday.day}`)
      .join(',')
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    set({ isSubmitting: true })
    try {
      const response = await postJson<{ message: string; line_friend_url?: string | null }>(
        buildLastMinuteInquiryUrl(window.location.pathname),
        {
          representative_name: formData.representativeName,
          email: formData.email,
          agreement: formData.agreement,
          plan_id: context.planId,
          start_date: context.startDate,
          end_date: context.endDate,
          number_of_adults: formData.numberOfAdults,
          number_of_study_abroad: formData.numberOfStudyAbroad,
          number_of_preschoolers_1: formData.numberOfPreschoolers1,
          number_of_students_1: formData.numberOfStudents1,
          birthdays,
          children: formData.studyAbroadChildren.map((child) => ({
            allergy_availability: child.allergyAvailability,
            allergy_detail: child.allergyDetail,
            school_experience: child.schoolExperience,
            support_needs: child.supportNeeds,
            support_needs_detail: child.supportNeedsDetail,
          })),
        },
        {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          },
        },
      )
      set({ isSubmitted: true, lineFriendUrl: response?.line_friend_url || null })
      return true
    } catch (error) {
      let message = '送信に失敗しました。しばらく時間をおいて再度お試しください。'
      if (error instanceof HttpError && error.serverErrorMessage) {
        message = error.serverErrorMessage
      }
      window.alert(message)
      return false
    } finally {
      set({ isSubmitting: false })
    }
  },
}))
